import React, { FC, useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "dayjs/locale/en-gb";
import "dayjs/locale/ar";
import { ar, enUS } from "date-fns/locale";
import styles from "./DateRangePicker.module.scss";
import { useRouter } from "next/router";

const formatDateRange = (
  value: [dayjs.Dayjs | null, dayjs.Dayjs | null],
  locale: string,
) => {
  if (!value[0] || !value[1]) return "";
  return `${value[0].locale(locale).format("ddd, DD MMM")} - ${value[1].locale(locale).format("ddd, DD MMM")}`;
};

interface DateRangePickerProps {
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  className?: string;
  onChange: (
    startDate: dayjs.Dayjs | null,
    endDate: dayjs.Dayjs | null,
  ) => void;
  dropdownIsOpen: boolean;
  isBannerSearch?: boolean;
}

const DateRangePickerComp: FC<DateRangePickerProps> = ({
  minDate,
  maxDate,
  startDate,
  endDate,
  className,
  onChange,
  dropdownIsOpen,
  isBannerSearch,
}) => {
  const router = useRouter();
  const locale = router.locale ?? "en";
  const today = dayjs().startOf("day");
  const [value, setValue] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    startDate,
    endDate,
  ]);
  const [valueOfDateRange, setValueOfDateRange] = useState<string>("");
  const [dateRangeUpdated, setDateRangeUpdated] = useState<boolean>(false);
  const [open, setOpen] = useState(dropdownIsOpen);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const [selectingStartDate, setSelectingStartDate] = useState<boolean>(true);
  const [monthsToShow, setMonthsToShow] = useState(2);

  useEffect(() => {
    const updateMonthsShown = () => {
      if (window.innerWidth <= 576) {
        setMonthsToShow(1);
      } else {
        setMonthsToShow(2);
      }
    };
    updateMonthsShown();
    window.addEventListener("resize", updateMonthsShown);
    return () => window.removeEventListener("resize", updateMonthsShown);
  }, []);

  useEffect(() => {
    setValueOfDateRange(formatDateRange(value, locale));
    onChange(value[0], value[1]);
    setOpen(false);
  }, [dateRangeUpdated, locale]);

  useEffect(() => {
    setValueOfDateRange(formatDateRange([startDate, endDate], locale));
  }, [endDate, locale]);

  useEffect(() => {
    setValue([startDate, endDate]);
  }, [startDate, endDate]);

  const handleInputClick = () => {
    setOpen((PREV) => !PREV);
  };

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [newStart, newEnd] = dates;
    const start = newStart ? dayjs(newStart) : null;
    const end = newEnd ? dayjs(newEnd) : null;
    if (selectingStartDate) {
      setValue([start, null]);
      setSelectingStartDate((prev) => !prev);
      return;
    } else {
      if (start && end && end.isSame(start, "day")) {
        setValue([start, start.add(1, "day")]);
        setDateRangeUpdated((prevState) => !prevState);
        setOpen((prevState) => !prevState);
        setSelectingStartDate((prev) => !prev);
        return;
      }
      if (end === null) {
        setValue([start, null]);
        return;
      }
      setValue((prev) => [prev[0], end]);
      setSelectingStartDate((prev) => !prev);
      setDateRangeUpdated((prevState) => !prevState);
      setOpen((prevState) => !prevState);
      return;
    }
  };
  const handleClickOutside = (event: MouseEvent) => {
    if (
      calendarRef.current &&
      inputRef.current &&
      !calendarRef.current.contains(event.target as Node) &&
      !inputRef.current.contains(event.target as Node)
    ) {
      event.stopPropagation();
      setOpen(false);
      if (selectingStartDate) {
        setValue((prev) =>
          prev[0] ? [prev[0], prev[0].add(1, "day")] : [null, null],
        );
        setSelectingStartDate((prev) => !prev);
        setDateRangeUpdated((prevState) => !prevState);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getDayClassName = (date: Date) => {
    const day = dayjs(date);
    if (value[0] && day.isSame(value[0], "day")) {
      return "custom-start-date";
    }
    if (value[1] && day.isSame(value[1], "day")) {
      return "custom-end-date";
    }
    return "";
  };
  useEffect(() => {
    setOpen(dropdownIsOpen);
  }, [dropdownIsOpen]);

  return (
    <div
      data-testid="CustomSearchField"
      onClick={() => {
        handleInputClick();
      }}
      ref={inputRef}
      role="button"
      tabIndex={0}
      aria-label="Open date range picker options"
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          handleInputClick();
        }
      }}
    >
      <div className={styles.content}>
        <div className="flex flex-col items-start w-full" ref={inputRef}>
          {!isBannerSearch && (
            <div className="relative w-full !cursor-pointer">
              <input
                id="dateRange"
                readOnly
                defaultValue={valueOfDateRange}
                className={`${styles.input} outline-none ${className}  min-w-[48px] min-h-6 max-c-md:!text-sm max-c-md:!leading-5`}
                placeholder="Select date range"
              />
            </div>
          )}
          {open && (
            <div
              className={styles.calendar}
              onClick={(event) => event.stopPropagation()}
              role="button"
              tabIndex={0}
              aria-label="Open date range calendar"
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.stopPropagation();
                }
              }}
            >
              <div
                ref={calendarRef}
                className="h-[14px] w-[14px] border-t border-l z-30 border-[#E5E4E3]  absolute top-[-6px] ltr:left-[20px] rtl:right-[20px] bg-white rotate-45"
              ></div>
              <DatePicker
                locale={locale === "ar" ? ar : enUS}
                selected={value[0]?.toDate() || null}
                startDate={value[0]?.toDate() || null}
                endDate={value[1]?.toDate() || null}
                onChange={handleDateRangeChange}
                selectsRange
                inline
                minDate={minDate ? minDate.toDate() : today.toDate()}
                maxDate={
                  maxDate ? maxDate.toDate() : today.add(1, "year").toDate()
                }
                monthsShown={monthsToShow}
                calendarClassName="z-20 "
                dayClassName={getDayClassName}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DateRangePickerComp;
